import { useMemo, useRef, useState } from 'react';
import {getPageDisplayIdentity} from '../shared';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGetCampaignPages, useGetPageById } from '@tinct/core-api';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@tinct/web-components';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  X,
} from 'lucide-react';
import { buildPageUrl } from '../../utils/build-page-url';

export function VariantPreviewPage() {
  const { id: campaignId = '', variantId = '' } = useParams<{
    id: string;
    variantId: string;
  }>();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: variant, isLoading } = useGetPageById({
    id: variantId,
    fields: {
      id: null,
      staticUrl: null,
      targetType: null,
      adCampaign: { fields: { id: null, name: null } },
      lead: {
        fields: {
          id: null,
          company: {
            fields: {
              id: null,
              name: null,
              logo: null,
              brandmarkLogo: null,
            },
          },
        },
      },
    },
  });

  const { data: pagesList } = useGetCampaignPages({
    staticParams: { campaignId },
    offset: 0,
    limit: 200,
    fields: {
      id: null,
      status: null,
      targetType: null,
      adCampaign: { fields: { id: null, name: null } },
      lead: {
        fields: {
          id: null,
          company: {
            fields: {
              id: null,
              name: null,
              logo: null,
              brandmarkLogo: null,
            },
          },
        },
      },
    },
  });

  const previewUrl = buildPageUrl(variant?.staticUrl, { preview: true });
  const backUrl = `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages`;

  const pages = useMemo(
    () => (pagesList?.data ?? []).filter((p) => p.status === 'published'),
    [pagesList],
  );
  const currentIndex = useMemo(
    () => pages.findIndex((p) => p.id === variantId),
    [pages, variantId],
  );
  const prevId = currentIndex > 0 ? (pages[currentIndex - 1]?.id ?? null) : null;
  const nextId =
    currentIndex >= 0 && currentIndex < pages.length - 1
      ? (pages[currentIndex + 1]?.id ?? null)
      : null;

  const previewRoute = (id: string) =>
    `${workspaceBaseUrl}/campaigns/${campaignId}/landing-pages/${id}/preview`;

  const companyName = variant
    ? getPageDisplayIdentity(variant).label
    : '—';
  const companyLogoUrl =
    variant?.lead?.company?.brandmarkLogo?.url ??
    variant?.lead?.company?.logo?.url ??
    undefined;
  const companyInitials = companyName.slice(0, 2).toUpperCase();

  const handlePickerSelect = (pageId: string) => {
    setPickerOpen(false);
    navigate(previewRoute(pageId));
  };

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* ── Top bar ── */}
      <div className="relative flex h-16 shrink-0 items-center justify-between border-b bg-background px-4">
        {/* Spacer to balance the right close button */}
        <div className="w-9 shrink-0" />

        {/* Center: page navigation (absolutely centred) */}
        <div className="absolute left-1/2 -translate-x-1/2 flex h-9 items-stretch rounded-md shadow-xs">
          {/* ← previous */}
          <Link
            to={prevId ? previewRoute(prevId) : '#'}
            aria-disabled={!prevId}
            aria-label="Previous page"
            className="flex items-center justify-center rounded-l-md border border-input bg-background px-3 text-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            <ChevronLeft className="size-4" />
          </Link>

          {/* Page picker */}
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Select page"
                className="flex h-full w-52 items-center gap-2 border-y border-r border-input bg-background px-3 text-sm transition-colors hover:bg-accent"
              >
                <CompanyAvatar
                  name={companyName}
                  initials={companyInitials}
                  logoUrl={companyLogoUrl}
                />
                <span className="flex-1 truncate text-left text-foreground">
                  {companyName}
                </span>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="center">
              <Command>
                <CommandInput placeholder="Search pages…" />
                <CommandList>
                  <CommandEmpty>No pages found.</CommandEmpty>
                  <CommandGroup>
                    {pages.map((page) => {
                      const name = page.lead?.company?.name ?? '—';
                      const logoUrl =
                        page.lead?.company?.brandmarkLogo?.url ??
                        page.lead?.company?.logo?.url ??
                        undefined;
                      const initials = name.slice(0, 2).toUpperCase();
                      return (
                        <CommandItem
                          key={page.id}
                          value={name}
                          onSelect={() =>
                            page.id && handlePickerSelect(page.id)
                          }
                        >
                          <CompanyAvatar
                            name={name}
                            initials={initials}
                            logoUrl={logoUrl}
                          />
                          <span className="flex-1 truncate">{name}</span>
                          {page.id === variantId && (
                            <Check className="ml-auto size-4 shrink-0" />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* → next */}
          <Link
            to={nextId ? previewRoute(nextId) : '#'}
            aria-disabled={!nextId}
            aria-label="Next page"
            className="flex items-center justify-center rounded-r-md border-y border-r border-input bg-background px-3 text-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            <ChevronRight className="size-4" />
          </Link>
        </div>

        {/* Right: close */}
        <Button variant="ghost" size="icon" className="size-9 shrink-0" asChild>
          <Link to={backUrl} aria-label="Close preview">
            <X className="size-4" />
          </Link>
        </Button>
      </div>

      {/* ── Iframe area ── */}
      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <span className="text-sm text-muted-foreground">
            Loading preview…
          </span>
        </div>
      )}

      {!isLoading && !previewUrl && (
        <div className="flex flex-1 items-center justify-center">
          <span className="text-sm text-muted-foreground">
            Preview not available
          </span>
        </div>
      )}

      {previewUrl && (
        <iframe
          ref={iframeRef}
          src={previewUrl}
          // The hosted page runs tenant / AI-generated HTML and scripts: it must not be able to
          // navigate this tab (no allow-top-navigation*) nor block it with dialogs (no
          // allow-modals). allow-same-origin changes nothing here — the page is cross-origin on
          // the CDN — and the referrer policy stays default because the SDK's preview mode keys
          // on document.referrer.
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          loading="eager"
          className="flex-1 w-full border-0"
          title="Page preview"
        />
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

type CompanyAvatarProps = {
  name: string;
  initials: string;
  logoUrl?: string;
};

function CompanyAvatar({ name, initials, logoUrl }: CompanyAvatarProps) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="size-5 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
      {initials}
    </span>
  );
}
